import { action, computed, observable } from 'mobx'
import { func, type IValidationSchema, validationContext } from 'validx'
import {
  QuestionEditorViewModelBase,
  type QuestionEditorViewModelOptions,
} from '@/features/legends/questions/QuestionEditorViewModelBase'
import type { PersonalOrganizerCollectedValueQuestion } from '@/features/legends/questions/questions'
import { OrganizerBindingQuestionEditorViewModelBase } from './OrganizerBindingQuestionEditorViewModelBase'

/**
 * A value collected by the personal tax organizer, which later elements
 * reference by ID like a variable. Its value is the organizer's value when
 * present, otherwise the formula's result, otherwise the binding type's
 * default.
 */
export class PersonalOrganizerCollectedValueQuestionEditorViewModel extends OrganizerBindingQuestionEditorViewModelBase<PersonalOrganizerCollectedValueQuestion> {
  private readonly _schema: IValidationSchema<PersonalOrganizerCollectedValueQuestionEditorViewModel> =
    {
      ...QuestionEditorViewModelBase.sharedSchema,
      collectBindingId: this._bindingIdValidators('collected value'),
      expr: [
        func(
          () => (this.expr.value ? this.expr.analyze() : true),
          'There are formula errors.',
        ),
      ],
    }

  readonly type = 'PersonalOrganizerCollectedValue'

  @observable accessor label = ''
  @observable accessor collectBindingId = ''

  readonly validation = validationContext(this, this._schema)

  constructor(options: QuestionEditorViewModelOptions) {
    super(options, false)
    this._validateWhileEditing(this._schema, {
      collectBindingId: ['expr'],
    })
  }

  /** @inheritDoc */
  get bindingId() {
    return this.collectBindingId
  }

  /** @inheritDoc */
  @action.bound
  setBindingId(id: string) {
    this.collectBindingId = id
  }

  /** @inheritDoc */
  @computed
  get catalog() {
    return this.dependencies.symbols.organizerBindings?.personal.collect ?? []
  }

  /**
   * The binding decides the type, so there is none until one is picked.
   */
  @computed
  get valueType() {
    return this.bindingValueType
  }

  /** @inheritDoc */
  get displayText() {
    return this.label || this.binding?.displayName || ''
  }

  /** @inheritDoc */
  @action
  hydrate(question: PersonalOrganizerCollectedValueQuestion) {
    this._hydrateShared(question)
    this.label = question.label ?? ''
    this.collectBindingId = question.collectBindingId ?? ''
    this.expr.setValue(question.expr)
  }

  /** @inheritDoc */
  protected _serialize(): PersonalOrganizerCollectedValueQuestion {
    return {
      ...this._serializeShared(),
      type: this.type,
      label: this.label,
      collectBindingId: this.collectBindingId,
      expr: this.expr.toJSON(),
    }
  }
}
